<template>
	<div class="w-full flex flex-col justify-center">
    <div v-if="hasCollections && state.isOwner"
         class="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-1 mb-4 sm:mb-2">
      <div class="review-search-form flex flex-col lg:flex-row">
        <div class="flex flex-1 flex-col lg:flex-row w-full lg:w-2/3 lg:mb-0 justify-between items-center">
          <div class="flex flex-col w-full justify-between sm:flex-row">
            <div class='comment-order-type pr-2'>
              <input type="radio"
                      name='comment-order-type'
                      class='hidden'
                      id='order_type_latest'
                      value="latest"
                      :checked="state.visibilityFilter == 'all'">
              <label class='text-grey-3' for='order_type_latest' @click="updateVisibilityFilter('all')">
                {{ $t('post_index_collection.all') }} ({{ allCollections.length }})
              </label>
              <span class='text-grey-3'>|</span>
              <input type="radio"
                      name='comment-order-type'
                      class='hidden'
                      id='order_type_positive'
                      value="public"
                      :checked="state.visibilityFilter == 'public'">
              <label class='text-grey-3' for='order_type_positive' @click="updateVisibilityFilter('public')">
                {{ $t('models.base_form.enabled.enabled_true') }} ({{ allCollections.filter(collection => collection.is_public).length }})
              </label>
              <span class='text-grey-3'>|</span>
              <input type="radio"
                      name='comment-order-type'
                      class='hidden'
                      id='order_type_negative'
                      value="private"
                      :checked="state.visibilityFilter == 'private'">
              <label class='text-grey-3' for='order_type_negative' @click="updateVisibilityFilter('private')">
                {{ $t('models.base_form.enabled.enabled_false') }} ({{ allCollections.filter(collection => !collection.is_public).length }})
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-row items-center gap-x-2">
        <div v-if="state.isOwner && hasCollections"
            class="flex flex-row items-center gap-x-2">
          <div class="icon sort-icon"></div>
          <a href="collections/sorting"
            class="leading-[26px] text-[12px] hover:text-gray-200">
            {{ $t(`public_profile_layout_component.collection_sort`) }}
          </a>
        </div>
        <span class="border-l border-gray-400 h-full hidden lg:block"></span>
        <a href="collections/new"
            class="w-full sm:w-auto mt-2 sm:mt-0 flex items-center justify-center text-14px btn btn-secondary py-1 rounded-[20px]">
          <div class="w-3 h-3 brightness-150 transform -rotate-45 icon close mr-2"></div>
          {{ $t('post_index_collection.new_collection') }}
        </a>
      </div>
    </div>
    <div id="designer-post-collections">
      <template v-if="hasCollections">
        <designer-collection-item
          v-for="collection in visibleCollections"
          :key="collection.id"
          :collection="collection"
          :is_owner="state.isOwner"
          @collection-deleted="handleCollectionDeleted">
        </designer-collection-item>
      </template>
      <template v-else-if="state.loading">
        <div class="post-item-loading flex flex-col justify-center items-center w-full h-[50vh] col-span-10">
          <img src="../../../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col justify-center items-center w-full h-[50vh] col-span-10">
          <img src="../../../../images/icons/archive.svg" class="w-16 h-16" />
          <div class='my-6 text-16px font-bold text-center'>
            {{ $t('post_index_collection.no_collection_list') }}
          </div>
          <a v-if="state.isOwner" href="collections/new"
              class="flex items-center justify-center text-14px btn btn-secondary rounded-[20px]">
            <div class="w-3 h-3 brightness-150 transform -rotate-45 icon close mr-2"></div>
            {{ $t('post_index_collection.new_collection') }}
          </a>
        </div>
      </template>
    </div>
	</div>
</template>

<script>
import { defineComponent, reactive, computed, onMounted, onUnmounted } from "vue";

export default defineComponent({
  name: 'DesignerCollectionIndex',

  setup() {
    const state = reactive({
      visibilityFilter: 'all',
      rawCollections: [],
      sortedCollections: [],
      currentPage: 1,
      perPage: 20,
      totalPages: 0,
      loading: false,
      hasMore: true,
      username: '',
      totalCount: 0,
      isOwner: false,
      sortOrderIds: []
    });

    // 基於排序後的集合計算
    const allCollections = computed(() => state.sortedCollections);

    const hasCollections = computed(() => state.sortedCollections.length > 0);

    const visibleCollections = computed(() => {
      switch(state.visibilityFilter) {
        case 'public':
          return state.sortedCollections.filter(collection => collection.is_public);
        case 'private':
          return state.sortedCollections.filter(collection => !collection.is_public);
        default:
          return state.sortedCollections;
      }
    });

    const getUsername = () => {
      const url = window.location.pathname;
      const match = url.match(/\/p\/([^\/]+)/);
      return match ? match[1] : '';
    };

    // 獲取排序配置
    const fetchSortOrderConfig = async () => {
      try {
        const response = await fetch('/api/v1/profile/orders_config?mode=profile_collections');
        if (response.ok) {
          const data = await response.json();
          state.sortOrderIds = (data.normal || []).map(id => Number(id));
          return state.sortOrderIds;
        }
      } catch (error) {
        console.error('Error fetching sort order config:', error);
        return [];
      }
    };

    // 根據排序配置重新排列集合
    const applySortOrder = (collections, sortOrderIds) => {
      if (sortOrderIds.length === 0) {
        // 如果沒有排序配置，保持原始順序
        return collections.map((item, index) => ({
          ...item,
          id: Number(item.id),
          sortOrder: index
        }));
      }

      return collections.map(collection => {
        const collectionId = Number(collection.id);
        const orderIndex = sortOrderIds.indexOf(collectionId);

        return {
          ...collection,
          id: collectionId,
          // 如果不在排序列表中，放到最後
          sortOrder: orderIndex >= 0 ? orderIndex : sortOrderIds.length + collectionId
        };
      }).sort((a, b) => a.sortOrder - b.sortOrder);
    };

    const fetchCollections = async (page = 1) => {
      if (state.loading || !state.hasMore) return;

      state.loading = true;
      const url = `/api/v1/profiles/${state.username}/get_designer_collections`;

      const params = new URLSearchParams({
        mode: 'page',
        page: page,
        per_page: state.perPage
      });

      try {
        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();

          if (page === 1) {
            state.rawCollections = data.data;
          } else {
            state.rawCollections.push(...data.data);
          }

          state.totalPages = data.pagination.total_pages;
          state.currentPage = data.pagination.current_page;
          state.totalCount = data.pagination.total_count;
          state.isOwner = data.is_owner;
          state.hasMore = state.currentPage < state.totalPages;

          // 每次載入數據後重新應用排序
          rebuildSortedCollections();
        }
      } catch (error) {
        console.error('Error fetching collections:', error);
      } finally {
        state.loading = false;
      }
    };

    // 重建排序後的集合數據
    const rebuildSortedCollections = () => {
      state.sortedCollections = applySortOrder(state.rawCollections, state.sortOrderIds);
    };

    // 初始化函數
    const initializeData = async () => {
      try {
        state.username = getUsername();

        // 並行載入排序配置和第一頁數據
        const [sortOrderIds] = await Promise.all([
          fetchSortOrderConfig(),
          fetchCollections(1)
        ]);
      } catch (error) {
        console.error('Error initializing data:', error);
      }
    };

    const handleScroll = () => {
      if (!state.hasMore || state.loading) return;

      const scrollableHeight = document.body.scrollHeight - window.innerHeight - 150;
      const scrolledDistance = Math.max(
				window.pageYOffset,
				document.documentElement.scrollTop,
				document.body.scrollTop
			);

			if (scrolledDistance >= scrollableHeight) {
				if (state.loading == false) {
          fetchCollections(state.currentPage + 1);
				}
			}
    };

    const throttledScroll = (fn, delay = 200) => {
      let lastCall = 0;
      return function (...args) {
        const now = Date.now();
        if (now - lastCall >= delay) {
          fn.apply(this, args);
          lastCall = now;
        }
      };
    };

    const updateVisibilityFilter = (filterType) => {
      if (state.visibilityFilter === filterType) return;
      state.visibilityFilter = filterType;
    };

    // 處理集合刪除，同時更新兩個數組
    const handleCollectionDeleted = (collectionId) => {
      const numericId = Number(collectionId);

      // 從原始數據中移除
      state.rawCollections = state.rawCollections.filter(collection => Number(collection.id) !== numericId);

      // 從排序數據中移除
      state.sortedCollections = state.sortedCollections.filter(collection => Number(collection.id) !== numericId);

      // 更新計數器
      const countElement = document.querySelector('.collections-count');
      if (countElement) {
        countElement.textContent = `(${state.sortedCollections.length})`;
      }
    };

    onMounted(() => {
      // 初始化函數
      initializeData();

      const throttledHandler = throttledScroll(handleScroll);

      if (window.isFirefox()) {
        window.addEventListener('scroll', throttledHandler, true);
      } else {
        window.addEventListener('scroll', throttledHandler);
      }
    });

    onUnmounted(() => {
      const throttledHandler = throttledScroll(handleScroll);

      if (window.isFirefox()) {
        window.removeEventListener('scroll', throttledHandler, true);
      } else {
        window.removeEventListener('scroll', throttledHandler);
      }
    });

    return {
      state,
      allCollections,
      hasCollections,
      visibleCollections,
      fetchCollections,
      updateVisibilityFilter,
      handleCollectionDeleted
    };
  }
});
</script>